<script setup>
// NOTICE: KAPTION-INTEGRATION
// Reusable drag-and-drop / click-to-browse media upload component.
// Uploads files to kapt.io via KaptionTemplateAttachmentsAPI and emits the
// resulting public URL. Also supports manual URL paste as a fallback.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KaptionTemplateAttachmentsAPI from 'dashboard/api/kaptionTemplateAttachments';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  mediaType: { type: String, default: 'image' }, // image | video | document
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// --- Constants ---
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

const ACCEPT_MAP = {
  image: {
    accept: '.jpg,.jpeg,.png,.gif,.webp',
    mimes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
  video: { accept: '.mp4,.mov', mimes: ['video/mp4', 'video/quicktime'] },
  document: { accept: '.pdf', mimes: ['application/pdf'] },
};

const acceptConfig = computed(
  () => ACCEPT_MAP[props.mediaType] || ACCEPT_MAP.image
);

// --- State ---
const isDragging = ref(false);
const isUploading = ref(false);
const uploadError = ref('');
const uploadedFilename = ref('');
const showUrlInput = ref(false);
const urlInputValue = ref('');
const fileInputRef = ref(null);

// A local preview data-URL for images (shown immediately while uploading)
const localPreview = ref('');

const hasValue = computed(() => !!props.modelValue);

const isImage = computed(() => props.mediaType === 'image');

const mediaIcon = computed(() => {
  if (props.mediaType === 'video') return 'i-lucide-film';
  if (props.mediaType === 'document') return 'i-lucide-file-text';
  return 'i-lucide-image';
});

// --- File validation ---
function validateFile(file) {
  if (!file) return 'No file selected';
  if (file.size > MAX_FILE_SIZE) return 'File exceeds 15 MB limit';
  if (!acceptConfig.value.mimes.includes(file.type)) {
    return `Unsupported file type: ${file.type}`;
  }
  return null;
}

// --- Upload ---
async function uploadFile(file) {
  const error = validateFile(file);
  if (error) {
    useAlert(error);
    return;
  }

  uploadError.value = '';
  isUploading.value = true;
  uploadedFilename.value = file.name;

  // Create an instant local preview for images
  if (isImage.value) {
    const reader = new FileReader();
    reader.onload = e => {
      localPreview.value = e.target?.result || '';
    };
    reader.readAsDataURL(file);
  }

  try {
    const { data } = await KaptionTemplateAttachmentsAPI.upload(file);
    emit('update:modelValue', data.url);
  } catch (err) {
    const msg = err?.response?.data?.error || err.message || 'Upload failed';
    uploadError.value = msg;
    useAlert(msg);
    // Clear any partial state
    localPreview.value = '';
    uploadedFilename.value = '';
  } finally {
    isUploading.value = false;
  }
}

// --- Drag & Drop handlers ---
function onDragEnter(e) {
  e.preventDefault();
  e.stopPropagation();
  if (!props.disabled) isDragging.value = true;
}
function onDragLeave(e) {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = false;
}
function onDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
}
function onDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = false;
  if (props.disabled) return;
  const file = e.dataTransfer?.files?.[0];
  if (file) uploadFile(file);
}

// --- Click to browse ---
function openFilePicker() {
  fileInputRef.value?.click();
}
function onFileChange(e) {
  const file = e.target?.files?.[0];
  if (file) uploadFile(file);
  // Reset so the same file can be re-selected
  if (fileInputRef.value) fileInputRef.value.value = '';
}

// --- URL paste ---
function applyUrl() {
  const url = urlInputValue.value.trim();
  if (!url) return;
  emit('update:modelValue', url);
  showUrlInput.value = false;
  uploadedFilename.value = '';
  localPreview.value = '';
}

// --- Remove ---
function removeMedia() {
  emit('update:modelValue', '');
  localPreview.value = '';
  uploadedFilename.value = '';
  uploadError.value = '';
  urlInputValue.value = '';
}

// --- Toggle URL mode ---
function toggleUrlInput() {
  showUrlInput.value = !showUrlInput.value;
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Uploaded / has-value state -->
    <div
      v-if="hasValue && !isUploading"
      class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
    >
      <!-- Thumbnail / icon -->
      <div class="flex-shrink-0">
        <img
          v-if="isImage && (localPreview || modelValue)"
          :src="localPreview || modelValue"
          alt=""
          class="h-14 w-14 rounded-md object-cover"
        />
        <div
          v-else
          class="flex h-14 w-14 items-center justify-center rounded-md bg-n-alpha-black2"
        >
          <Icon :icon="mediaIcon" class="size-6 text-n-slate-11" />
        </div>
      </div>

      <!-- File info -->
      <div class="flex flex-1 flex-col gap-0.5 min-w-0">
        <span
          v-if="uploadedFilename"
          class="truncate text-sm font-medium text-n-slate-12"
        >
          {{ uploadedFilename }}
        </span>
        <span class="truncate text-xs text-n-slate-11">
          {{ modelValue }}
        </span>
      </div>

      <!-- Remove button -->
      <button
        type="button"
        class="flex-shrink-0 rounded p-1.5 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors"
        :disabled="disabled"
        @click="removeMedia"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
    </div>

    <!-- Uploading state -->
    <div
      v-else-if="isUploading"
      class="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-n-brand bg-n-alpha-brand1 p-6"
    >
      <div class="flex items-center gap-3">
        <Spinner size="small" />
        <span class="text-sm text-n-slate-12">
          {{
            t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.UPLOADING', {
              filename: uploadedFilename,
            })
          }}
        </span>
      </div>
      <!-- Quick image preview while uploading -->
      <img
        v-if="isImage && localPreview"
        :src="localPreview"
        alt=""
        class="h-20 rounded-md object-cover opacity-60"
      />
    </div>

    <!-- Drop zone (idle state) -->
    <div v-else>
      <div
        class="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center transition-colors cursor-pointer"
        :class="[
          isDragging
            ? 'border-n-brand bg-n-alpha-brand1'
            : 'border-n-weak hover:border-n-slate-7 hover:bg-n-alpha-black2',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ]"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @dragover="onDragOver"
        @drop="onDrop"
        @click="!disabled && openFilePicker()"
      >
        <Icon
          :icon="isDragging ? 'i-lucide-download' : mediaIcon"
          class="size-8 text-n-slate-9"
        />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              isDragging
                ? t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.DROP_ACTIVE')
                : t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.DROP_IDLE')
            }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.MAX_SIZE', {
                formats: acceptConfig.accept,
              })
            }}
          </span>
        </div>
      </div>

      <!-- Error display -->
      <p v-if="uploadError" class="mt-1 text-xs text-n-ruby-11">
        {{ uploadError }}
      </p>

      <!-- Toggle to URL paste -->
      <button
        type="button"
        class="mt-1.5 inline-flex items-center gap-1 text-xs text-n-brand hover:text-n-brand-hover transition-colors"
        @click.stop="toggleUrlInput"
      >
        <Icon icon="i-lucide-link" class="size-3" />
        {{
          showUrlInput
            ? t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.HIDE_URL')
            : t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.PASTE_URL')
        }}
      </button>

      <!-- URL paste input -->
      <div v-if="showUrlInput" class="mt-2 flex gap-2">
        <input
          v-model="urlInputValue"
          type="url"
          :placeholder="
            t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.URL_PLACEHOLDER')
          "
          class="flex-1 h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          @keydown.enter="applyUrl"
        />
        <NextButton xs @click="applyUrl">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.MEDIA_UPLOAD.APPLY') }}
        </NextButton>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInputRef"
      type="file"
      :accept="acceptConfig.accept"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>
